// packages/web/src/pages/OrderDetailPage.tsx
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Package,
  Truck,
  CheckCircle2,
  CreditCard,
  Home,
  ChevronLeft,
  UserCheck,
  PackageCheck,
  Send,
  ChevronDown,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { formatPrice, getNotificationForStatus, getStatusStyle, getStatusTitle, OrderStatus, PaymentStatus } from '@bookstore/shared';
import OrderProductCard from '@/components/common/OrderProductCard';
import { useCancelOrder, useOrder, useOrderProducts } from '@/hooks/useOrders';
import { useState, useEffect } from 'react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { format } from 'date-fns';
import { getStatusIcon } from '@/utils/getStatusIcon';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'sonner';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const cancelOrderMutation = useCancelOrder();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const { data: order, isLoading: orderLoading, isError: orderError, refetch } = useOrder(orderId);
  const [showHistory, setShowHistory] = useState(false);
    const navigate = useNavigate();

  // 🔄 Automatic Recovery: Silently check if payment needs verification
  useEffect(() => {
    if (
      order &&
      order.paymentStatus === PaymentStatus.PENDING &&
      order.paymentMethod === 'razorpay' &&
      orderId
    ) {
      // Silently verify pending payment on page load (no UI interaction needed)
      const verifyPendingPayment = async () => {
        try {
          const functions = getFunctions(undefined, 'asia-south1');
          const verifyFn = httpsCallable(functions, 'verifyPendingPayment');
          const result = await verifyFn({ orderId });

          const data = result.data as any;

          if (data.recovered) {
            toast.success('Payment verified! Your order has been confirmed.');
            refetch();
          } else if (data.deleted) {
            // Order was deleted (failed payment or no payment attempted)
            toast.error('This order was cancelled - payment was not completed.');
            // Redirect to orders page after a moment
            setTimeout(() => navigate('/orders'), 2000);
          }
          // If still pending/processing, do nothing (webhook will handle it)
        } catch (error) {
          console.error('Silent payment verification failed:', error);
          // Silent failure - webhook will handle it
        }
      };

      verifyPendingPayment();
    }
  }, [order?.id]);

  // Fetch all products in parallel (stable hooks!)
  // In OrderDetailPage.tsx (replace the useQueries block)
  const {
    data: products,
    isLoading: productsLoading,
    isError: productsError,
  } = useOrderProducts(order?.items);

  const statusToStep: Record<OrderStatus, number> = {
    [OrderStatus.PROCESSING]: 2,
    [OrderStatus.PLACED]: 1,
    [OrderStatus.CONFIRMED]: 2,
    [OrderStatus.PACKED]: 3,
    [OrderStatus.PICK_UP_SCHEDULED]: 3, // Group with packed
    [OrderStatus.PICKED_UP]: 4,
    [OrderStatus.IN_TRANSIT]: 4, // Generic "In Transit" covers pickup/transit
    [OrderStatus.SHIPPED]: 4, // Grouped under transit
    [OrderStatus.OUT_FOR_DELIVERY]: 5,
    [OrderStatus.DELIVERED]: 6,
    [OrderStatus.CANCELLED]: 8,
    [OrderStatus.CANCELLATION_REQUESTED]: 8,
    [OrderStatus.RETURNED]: 6, // Or handle as post-delivery
  };



  const status = getStatusStyle(order?.orderStatus || OrderStatus.PLACED);

  const steps = [
    { id: 1, name: 'Order Placed', icon: Package, statusGroup: [OrderStatus.PLACED] },
    { id: 2, name: 'Processing', icon: UserCheck, statusGroup: [OrderStatus.CONFIRMED] },
    {
      id: 3,
      name: 'Packed',
      icon: PackageCheck,
      statusGroup: [OrderStatus.PACKED, OrderStatus.PICK_UP_SCHEDULED],
    },
    {
      id: 4,
      name: 'In Transit',
      icon: Truck,
      statusGroup: [OrderStatus.PICKED_UP, OrderStatus.IN_TRANSIT, OrderStatus.SHIPPED],
    },
    { id: 5, name: 'Out for Delivery', icon: Send, statusGroup: [OrderStatus.OUT_FOR_DELIVERY] },
    {
      id: 6,
      name: 'Delivered',
      icon: CheckCircle2,
      statusGroup: [OrderStatus.DELIVERED, OrderStatus.RETURNED],
    },
  ];

  const currentStep =
    steps.findIndex((s) => s.statusGroup.includes(order?.orderStatus || OrderStatus.PLACED)) ;
  const isCancelled = order?.orderStatus === OrderStatus.CANCELLED;

  // Early return for loading/error
  if (orderLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (orderError || !order) {
    return (
      <div className="min-h-screen bg-background-muted py-12">
        <div className="container-custom text-center">
          <h1 className="text-2xl font-semibold text-text">Order Not Found</h1>
          <Link to="/orders" className="btn-primary mt-4 inline-block" aria-label="orders">
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Order | Sunrise Bookstore</title>
      </Helmet>

      <div className="min-h-screen bg-background-muted py-12">
        <div className="container-custom space-y-10">
          {/* Back Button */}
          <Link
            to="/orders"
            className="inline-flex items-center text-primary hover:text-text transition"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Orders
          </Link>

          {/* cancellation dialogue  */}
          {order?.orderStatus === OrderStatus.CANCELLATION_REQUESTED && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-orange-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-orange-900 mb-1">Cancellation Request Pending</h3>
                  <p className="text-sm text-orange-800">
                    Your cancellation request has been submitted and is awaiting admin approval.
                    We'll notify you once it's been reviewed.
                  </p>
                  {order.cancellationReason && (
                    <p className="text-sm text-orange-700 mt-2">
                      <strong>Your reason:</strong> {order.cancellationReason}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}


          {/* Header */}
          <div
            className="
            bg-background-soft rounded-2xl shadow-sm border border-border 
            p-6 flex flex-wrap items-center justify-between gap-4
          "
          >
            <div>
              <h1 className="text-2xl font-semibold text-text">Order #{order?.id}</h1>
              <p className="text-sm text-text-secondary mt-1">
                Placed on{' '}
                {format(new Date(order?.createdAt?.toDate() || Date.now()), 'do MMM yyyy h:mma')}
              </p>
            </div>

                      <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium ${status.bg} ${status.color}`}
          >

            {/* convert icon string to icons */}
             {getStatusIcon(status.icon)}            
            {status.label}
          </div>
          </div>

          {/* 🔄 Pending Payment Warning */}
          {/* {order?.paymentStatus =Info */} 
          {order?.paymentStatus === PaymentStatus.PENDING && order?.paymentMethod === 'razorpay' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <Loader2 className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5 animate-spin" />
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-900 mb-1">
                    Verifying Payment...
                  </h3>
                  <p className="text-sm text-yellow-800">
                    We're confirming your payment with the payment gateway. This usually takes a few seconds.
                    If you completed the payment, your order will be confirmed automatically.
                  </p>
                  <p className="text-xs text-yellow-700 mt-2">
                    💡 If payment wasn't completed, this order will be automatically cancelled within a few minutes.
                  </p>
                  </div>
              </div>
            </div>
          )}

          {/* TIMELINE */}
          <div className="bg-background-soft rounded-2xl p-6 border border-border shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-lg font-semibold text-text">
                Delivery Progress{' '}
                {isCancelled && <span className="text-red-600 ml-2">(Cancelled)</span>}
              </h2>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                {showHistory ? 'Hide' : 'View'} History
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`}
                />
              </button>
            </div>

            {/* Simplified Horizontal Timeline */}

            {isCancelled ? (
              <div className="text-center text-red-600 font-semibold">
                This order has been cancelled.
              </div>
            ) : (
              <div className="flex items-center relative mb-6 overflow-x-auto pb-2">
                {steps.map((step, index) => {
                  const isCompleted = index <= currentStep;
                  const Icon = step.icon;

                  return (
                    <div
                      key={step.id}
                      className="flex flex-col items-center text-center flex-1 relative"
                    >
                      <div
                        className={`
                        w-12 h-12 flex items-center justify-center rounded-full
                        transition-all shadow-sm
                        ${
                          isCompleted
                            ? 'bg-primary-600 text-white shadow-primary-600/30'
                            : 'bg-background-subtle text-text-secondary'
                        }
                      `}
                      >
                        <Icon className="w-6 h-6" />
                      </div>

                      <p
                        className={`
                        mt-2 text-sm font-medium
                        ${isCompleted ? 'text-text' : 'text-text-secondary'}
                      `}
                      >
                        {order?.orderStatus === OrderStatus.CANCELLED && index === currentStep - 1
                          ? 'Cancelled'
                          : order?.orderStatus === OrderStatus.DELIVERED
                            ? 'Delivered'
                            : step.name === 'Out for Delivery & Delivered'
                              ? 'Out for Delivery'
                              : step.name}
                      </p>

                      {index < steps.length - 1 && (
                        <div
                          className={`
                          absolute top-6 left-[60%] w-full h-[2px]
                          ${isCompleted ? 'bg-primary-600' : 'bg-border'}
                        `}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Expandable History */}
            <div
              className={`${showHistory ? 'block' : 'hidden'} space-y-3 pt-4 border-t border-gray-200`}
            >
              <p className="text-sm text-gray-600">Status History:</p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {order?.statusHistory?.length ? (
                  order.statusHistory
                    .sort((a, b) => (b.timestamp.seconds || 0) - (a.timestamp.seconds || 0)) // Reverse chrono
                    .map((entry) => (
                      <div
                        key={entry.timestamp.seconds}
                        className="flex items-center gap-3 p-2 bg-background rounded-md"
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${
                            entry.status === OrderStatus.DELIVERED
                              ? 'bg-green-500'
                              : entry.status === OrderStatus.CANCELLED
                                ? 'bg-red-500'
                                : 'bg-primary'
                          }`}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{getStatusTitle(entry.status as OrderStatus)}</p>
                          {entry.note && <p className="text-xs text-gray-500">{entry.note}</p>}
                          {/* {entry.updatedBy && (
                            <p className="text-xs text-gray-400">By: {entry.updatedBy}</p>
                          )} */}
                        </div>
                        <p className="text-xs text-gray-500 whitespace-nowrap">
                          {format(new Date(entry.timestamp.toDate()), 'MMM dd, h:mma')}
                        </p>
                      </div>
                    ))
                ) : (
                  <p className="text-sm text-gray-500 italic">No history available yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* SHIPPING/TRACKING */}
          {!isCancelled && (
            <div className="bg-background-soft rounded-2xl p-6 border border-border shadow-sm">
              <h2 className="text-lg font-semibold text-text mb-4">Shipping Information</h2>
              
              {/* iCarry Provider */}
              {order?.shipping?.provider === 'icarry' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium uppercase">
                      {order.shipping.provider}
                    </span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-text-secondary uppercase font-medium">Courier</p>
                      <p className="font-medium text-text">{order.shipping.courierName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-secondary uppercase font-medium">AWB Number</p>
                      <p className="font-mono text-sm text-text">{order.shipping.awb || 'N/A'}</p>
                    </div>
                    {order.shipping.trackingStatus && (
                      <div>
                        <p className="text-xs text-text-secondary uppercase font-medium">Current Status</p>
                        <p className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium inline-block mt-1">
                          {order.shipping.trackingStatus}
                        </p>
                      </div>
                    )}
                    {order.shipping.estimatedDelivery && (
                      <div>
                        <p className="text-xs text-text-secondary uppercase font-medium">Estimated Delivery</p>
                        <p className="font-medium text-text">
                          {format(new Date(order.shipping.estimatedDelivery.toDate()), 'do MMM yyyy')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Tracking History */}
                  {order.shipping.trackingHistory && order.shipping.trackingHistory.length > 0 && (
                    <div className="mt-4 border-t border-border pt-4">
                      <p className="text-sm font-medium text-text mb-3">Tracking Updates</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {order.shipping.trackingHistory.map((entry: any, index: number) => (
                          <div key={index} className="bg-background p-3 rounded-lg border border-border">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm font-medium text-text">{entry.notes || entry.status}</p>
                                {entry.location && (
                                  <p className="text-xs text-text-secondary mt-1">📍 {entry.location}</p>
                                )}
                              </div>
                              <span className="text-xs text-text-secondary whitespace-nowrap ml-2">
                                {entry.timestamp && format(new Date(entry.timestamp), 'MMM dd, h:mma')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {order.shipping.trackingUrl && (
                    <a
                      href={order.shipping.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-medium bg-primary-600 hover:bg-primary-700 transition shadow-sm mt-4"
                    >
                      Track on iCarry
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              ) : (
                /* Manual Courier */
                <div className="flex flex-col sm:flex-row justify-between gap-6">
                  <div>
                    <p className="text-text-secondary">
                      Courier Partner:{' '}
                      <span className="font-medium text-text">{order?.courierPartner || 'N/A'}</span>
                    </p>
                    <p className="text-text-secondary">
                      Tracking ID:{' '}
                      <span className="font-medium text-text">
                        {order?.trackingId || order?.awbNumber || 'N/A'}
                      </span>
                    </p>
                    <p className="text-text-secondary">
                      Estimated Delivery:{' '}
                      <span className="font-medium text-text">
                        {order?.estimatedDelivery
                          ? format(new Date(order?.estimatedDelivery?.toDate()), 'do MMM yyyy')
                          : 'N/A'}
                      </span>
                    </p>
                  </div>

                  <Link
                    to="#"
                    className="px-5 py-2.5 rounded-xl text-white text-sm font-medium bg-primary-600 hover:bg-primary-700 transition shadow-sm h-10 flex items-center justify-center"
                  >
                    Track Package
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* ITEMS */}
          <div className="bg-background-soft rounded-2xl p-6 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-text mb-4">Items Ordered</h2>
            {order?.items.map((item) => {
              const product = products?.find((p) => p.id === item.productId);
              return (
                <div key={item.productId} className="py-4">
                  {productsLoading ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-6 bg-background-subtle rounded w-3/4"></div>
                      <div className="h-4 bg-background-subtle rounded w-1/2"></div>
                    </div>
                  ) : product ? (
                    <OrderProductCard
                      product={product}
                      quantity={item.quantity}
                      orderStatus={order.orderStatus}
                      deliveredOn={
                        format(
                          new Date(order?.estimatedDelivery?.toDate() || new Date()),
                          'do MMM yyyy'
                        ) || undefined
                      }
                    />
                  ) : (
                    <p>Product not found.</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* PAYMENT + ADDRESS */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Payment */}
            <div className="bg-background-soft p-6 rounded-2xl shadow-sm border border-border">
              <h3 className="text-lg font-semibold text-text mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-text-secondary" /> Payment
              </h3>
              <p className="text-text">
                Method: <span className="font-medium">{order?.paymentMethod}</span>
              </p>
              <p className="text-text">
                Total Paid:{' '}
                <span className="font-semibold text-primary-700">
                  {formatPrice(order?.totalAmount || order?.subtotal)}
                </span>
              </p>
            </div>

            {/* Address */}
            <div className="bg-background-soft p-6 rounded-2xl shadow-sm border border-border">
              <h3 className="text-lg font-semibold text-text mb-3 flex items-center gap-2">
                <Home className="w-5 h-5 text-text-secondary" /> Delivery Address
              </h3>
              <p className="text-text font-medium">{order?.shippingAddress?.name},</p>
              <p className="text-text-secondary">
                {order?.shippingAddress?.addressLine1}, {order?.shippingAddress?.addressLine2},{' '}
                {order?.shippingAddress?.city}, {order?.shippingAddress?.state} -{' '}
                {order?.shippingAddress?.pincode}
              </p>
              <p className="text-text-secondary">📞 {order?.shippingAddress?.phone}</p>
              {order?.shippingAddress?.secondaryPhone && (
                <p className="text-text-secondary">📞 {order?.shippingAddress?.secondaryPhone} (Secondary)</p>
              )}
            </div>
          </div>


          {/* Cancel Order */}
          { 
          statusToStep[order?.orderStatus || OrderStatus.PLACED] < 4 && (order?.orderStatus !== OrderStatus.CANCELLED) && (order?.orderStatus !== OrderStatus.CANCELLATION_REQUESTED)
            && (
              
              <button
                onClick={() => setShowCancelDialog(true)}
                disabled={cancelOrderMutation.isPending}
                className="px-5 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition disabled:opacity-50"
              >
                {cancelOrderMutation.isPending ? 'Submitting...' : 'Request Cancellation'}
              </button>
            )
            
            }

          <ConfirmDialog
            isOpen={showCancelDialog}
            onClose={() => setShowCancelDialog(false)}
          title="Request Cancellation?" //  Updated title
          message="Please provide a reason for cancellation. Your request will be reviewed by our team."  //  Updated message
          confirmText="Request Cancellation" //  Updated button text
          inputEnabled={true}
          inputRequired={true}
          inputLabel="Cancellation reason"
          inputPlaceholder="E.g., changed my mind, found better price, etc."
          onConfirm={(reason) => {
            cancelOrderMutation.mutate({
              orderId: order?.id!!,
              reason: reason!!,
              initiatedBy: 'customer', //  This is already correct in your code!
            });
          }}
          />
        </div>
      </div>
    </>
  );
};

export default OrderDetailPage;
